export const PAYMENT_STATUSES = {
  PREPARATION: 'PREPARATION',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVAL_REJECTED: 'APPROVAL_REJECTED',
  APPROVED: 'APPROVED',
  PROCESSING: 'PROCESSING',
  PAID: 'PAID',
  PROCESSING_VOID: 'PROCESSING_VOID',
  VOIDED: 'VOIDED',
  DECLINED: 'DECLINED',
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUSES)[keyof typeof PAYMENT_STATUSES];

// DPM-646: a payment can be deleted until money movement starts — once it is PROCESSING
// (or further: PAID/VOIDED/DECLINED) deletion is no longer allowed.
const DELETABLE_PAYMENT_STATUSES: readonly PaymentStatus[] = [
  PAYMENT_STATUSES.PREPARATION,
  PAYMENT_STATUSES.PENDING_APPROVAL,
  PAYMENT_STATUSES.APPROVAL_REJECTED,
  PAYMENT_STATUSES.APPROVED,
];

export const isPaymentDeletable = (status?: string): boolean =>
  (DELETABLE_PAYMENT_STATUSES as readonly string[]).includes(status ?? '');

export const PAYMENT_TAGS = {
  saveForLater: '_S_SAVE_FOR_LATER',
  archive: '_S_ARCHIVED',
  hasRecreatedPayment: '_S_HAS_RECREATED_PAYMENT',
} as const;

export type PaymentTagKey = keyof typeof PAYMENT_TAGS;
export type PaymentTagValue = (typeof PAYMENT_TAGS)[PaymentTagKey];

export const PAYMENT_TAG_LABELS = Object.fromEntries(
  Object.entries(PAYMENT_TAGS).map(([key, value]) => [value, key]),
) as Record<string, string>;

/**
 * Hard limit of the payment `transferReason` — it is mapped to the description /
 * remittance field of the payment instruction sent to the bank, which caps it at
 * 34 characters.
 */
export const TRANSFER_REASON_MAX_LENGTH = 34;

/**
 * Characters the bank rejects in the payment description — only letters (any script,
 * so Hebrew/Latin both work), digits and spaces are allowed through.
 */
const TRANSFER_REASON_FORBIDDEN_CHARS = /[^\p{L}\p{N} ]/gu;

export const sanitizeTransferReason = (value: string): string => value.replace(TRANSFER_REASON_FORBIDDEN_CHARS, '');
